// QR Kiosk v2 — dashboard header machine card (designer `ScenarioHeader`).
// Pass 1b 2026-07-24 (KV2_DESIGN_REVIEW_FEEDBACK.md §1b.7): the identity row
// (image + name + S/N) is now a BUTTON that opens the spec sheet (…/details) —
// it replaces the removed "Detaily stroja" tile. The (i) glyph pinned to the
// card's top-right corner is the affordance. Serial renders in the sans stack,
// explicitly non-italic (§1b.1 — italic is banned kiosk-wide). The status pill
// + alarm strip render ONLY from the `liveStatus` prop (PG-1 degrade-to-absent:
// with liveStatus undefined the DOM carries no pill, no strip, never faked).
// Tokens only; no MUI / dt / sx.
import React from "react";
import { useNavigate } from "react-router-dom";
import { Machine } from "../../models/machinesModels";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName, getMachineName } from "../../../shared/utils/machineUtils";
import { SilhouettePlate } from "../shared/KioskSilhouette";
import { StatusPip, PipTone } from "../shared/StatusPip";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { KioskLiveStatus, getKioskMachineImageUrl } from "./kioskLiveStatus";

export interface DashboardHeaderCardProps {
  machine: Machine; // the ROUTE-SCOPED machine (§3.1)
  liveStatus?: KioskLiveStatus; // ABSENT until B4-T4 wires the hook. NEVER faked.
}

interface PillConfig {
  labelKey: string;
  classes: string;
  tone: PipTone;
}

// Render mapping (resolves B4 OQ-1 — recorded decision KV2-1-f). undefined /
// available:false / status "Unknown" → NOTHING (identical DOM to pre-B4).
function resolvePill(liveStatus?: KioskLiveStatus): PillConfig | null {
  if (!liveStatus || !liveStatus.available) return null;
  switch (liveStatus.status) {
    case "Running":
      return { labelKey: "kioskV2StatusRunning", classes: "stat-run-bg stat-run-bd stat-run-fg", tone: "--green" };
    case "Idle":
      return { labelKey: "kioskV2StatusIdle", classes: "stat-idl-bg stat-idl-bd stat-idl-fg", tone: "--amber" };
    case "Down":
      return { labelKey: "kioskV2StatusDown", classes: "stat-err-bg stat-err-bd stat-err-fg", tone: "--red" };
    default:
      return null; // "Unknown" or undefined
  }
}

// Local HH:mm (24h) from a UTC ISO string — same getHours/getMinutes pad-2
// technique as the committed OfflineStrip.formatTime (no offset math, no moment).
function formatLocalTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function DashboardHeaderCard({ machine, liveStatus }: DashboardHeaderCardProps) {
  const navigate = useNavigate();
  const { translate } = useTranslation("public");
  const press = usePressed();

  const imageUrl = getKioskMachineImageUrl(machine);
  const title = getMachineDisplayName(machine);
  const subtitle = getMachineName(machine);
  const pill = resolvePill(liveStatus);
  const alarm = liveStatus?.alarm;

  // §3.5 nav guard — never emit /public-v2/machines/undefined/…
  const openSpecSheet = () => {
    if (machine.internalNumber) navigate(`/public-v2/machines/${machine.internalNumber}/details`);
  };

  return (
    <div
      style={{
        margin: "12px 14px 0",
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        boxShadow: "var(--shadow)",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {/* Identity row = spec-sheet button (§1b.7). paddingRight clears the corner (i). */}
      <button
        type="button"
        {...press.pressHandlers}
        onClick={openSpecSheet}
        aria-label={translate("kioskMachineDetails")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          width: "100%",
          padding: "14px 44px 14px 14px",
          position: "relative",
          border: 0,
          background: "transparent",
          textAlign: "left",
          fontFamily: "inherit",
          cursor: "pointer",
          ...press.pressStyle,
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            style={{
              width: 78,
              height: 58,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid var(--border-soft)",
              flexShrink: 0,
            }}
          />
        ) : (
          <SilhouettePlate w={78} h={58} note={null} />
        )}

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 19,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 600,
                color: "var(--ink-2)",
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subtitle}
            </div>
          )}
          {machine.serialNumber && (
            <div
              style={{
                // §1b.1 — NO italic, NO mono divergence: same sans stack as the
                // title/subtitle, explicit upright style, one step below subtitle.
                fontStyle: "normal",
                fontSize: 14,
                fontWeight: 600,
                color: "var(--ink-2)",
                marginTop: 3,
              }}
            >
              {`${translate("serialNumber")} ${machine.serialNumber}`}
            </div>
          )}
        </div>

        {pill && (
          <span
            className={pill.classes}
            style={{
              border: "1px solid",
              borderRadius: 999,
              height: 28,
              padding: "0 11px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12.5,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            <StatusPip tone={pill.tone} size={8} />
            {translate(pill.labelKey)}
          </span>
        )}

        {/* (i) affordance — pinned to the card's top-right corner (§1b.7). */}
        <span
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "var(--muted)",
            display: "inline-flex",
          }}
        >
          <Glyph d={GD.info} size={22} />
        </span>
      </button>

      {alarm != null && (
        <div
          style={{
            padding: "10px 14px",
            borderTop: "1px solid var(--border-soft)",
            background: "color-mix(in oklab, var(--red) 6%, var(--card))",
          }}
        >
          <div style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.45 }}>
            {`${translate("kioskV2Alarm")} `}
            <b style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>{alarm.code}</b>
            {` · ${translate("kioskV2Since", { time: formatLocalTime(alarm.since) })}`}
          </div>
          {alarm.message && (
            <div
              style={{
                fontSize: 13,
                color: "var(--muted)",
                marginTop: 2,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {alarm.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DashboardHeaderCard;
